import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Modal,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ReportService from "../../../services/reportService";
import mapicon from "../../../assets/Dashboard/mapicon.svg";
import empty from "../../../assets/empty.png";

const COLORS = ["#66BB6A", "#FFA726", "#EF5350", "#42A5F5", "#AB47BC"];

const WorkerAttendanceReport = ({ dateRange }) => {
  const { user } = useSelector((state) => state.auth);
  const companyId = user?.company_id;
  const workerId = user?.id;
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [chartData, setChartData] = useState({ bar: [], pie: [] });
  const [summary, setSummary] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);

  const fetchReport = async () => {
    if (!dateRange.startDate || !dateRange.endDate || !companyId || !workerId) return;

    setLoading(true);
    try {
      const response = await ReportService.getAttendanceReport(
        dateRange.startDate,
        dateRange.endDate,
        workerId,
        companyId
      );

      if (!response.error && response.data?.data) {
        const data = response.data.data;
        setReportData(data);
        setSummary({
          records_count: response.data.records_count,
          generated_at: response.data.generated_at,
        });

        // Process data for charts — the API's punch-level summary (validated/pending_review/not_validated
        // counts across all punches) is the authoritative validation breakdown, distinct from the
        // per-day rows in `data` (one row per day, not one per punch).
        processChartData(data, response.data.summary);
      } else {
        setReportData([]);
        setChartData({ bar: [], pie: [] });
        setSummary(null);
      }
    } catch (e) {
      console.error("Error fetching attendance report:", e);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Keys here are stable and language-independent — translation happens only at render time
  // (via `name`/label lookups), so switching language doesn't require refetching or desync the chart.
  const processChartData = (data, apiSummary) => {
    let totalHours = 0;
    let validatedCount = 0;
    let pendingCount = 0;
    let invalidCount = 0;

    // Bar chart data - individual dates (same as table)
    const barData = data.map((record) => {
      const hours = Number((parseFloat(record.total_hours) || 0).toFixed(2));
      totalHours += hours;

      // Count validation statuses (fallback only — used when the API doesn't return a punch-level summary)
      const status = record.validation_status?.toLowerCase();
      if (status === "validated") validatedCount++;
      else if (status === "pending_review" || status === "pending") pendingCount++;
      else invalidCount++;

      return {
        name: new Date(record.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        hours,
        fullDate: record.date,
      };
    });

    // Pie chart data - validation status distribution.
    // Prefer the API's punch-level summary (counts every punch) over counting `data` rows
    // (one row per day), since a single day can contain multiple punches with different statuses.
    const pieData = apiSummary
      ? [
          { status: "validated", value: apiSummary.validated || 0 },
          { status: "pending", value: apiSummary.pending_review || 0 },
          { status: "invalid", value: apiSummary.not_validated || 0 },
        ].filter((d) => d.value > 0)
      : [
          { status: "validated", value: validatedCount },
          { status: "pending", value: pendingCount },
          { status: "invalid", value: invalidCount },
        ].filter((d) => d.value > 0);

    setChartData({ bar: barData, pie: pieData, totalHours: totalHours.toFixed(2) });
  };

  useEffect(() => {
    fetchReport();
  }, [dateRange.startDate, dateRange.endDate, companyId, workerId]);

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Pie slice label styled to match the card header font (Poppins) instead of Recharts' default
  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 22;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        style={{ fontFamily: "Poppins, sans-serif", fontSize: 12, fontWeight: 500, fill: "#44546F" }}
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return "-";
    return `${parseFloat(hours).toFixed(2)} hrs`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "validated":
        return { bg: "#E8F5E9", color: "#2E7D32" };
      case "pending_review":
      case "pending":
        return { bg: "#FFF3E0", color: "#E65100" };
      default:
        return { bg: "#FFEBEE", color: "#C62828" };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            color: "#003149",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          {t("reportsScreen.hourlyComplianceReportAndEmployeeLog")}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "#6B7280",
            fontFamily: "Poppins, sans-serif",
            mt: 0.5,
          }}
        >
         {/* {t("reportsScreen.attendanceApiDesc")} */}
          {t("reportsScreen.attendanceApiDesc")
    .split(",")
    .map(item =>
      item
        .trim()
        .replace(/_/g, " ")
        .replace(/\b\w/g, char => char.toUpperCase())
    )
    .join(", ")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {/* Bar Chart - Daily Hours */}
        <Card
          sx={{
            flex: "1 1 48%",
            minWidth: 300,
            p: 2,
            border: "2px solid #091E4224",
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#44546F",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {t("reportsScreen.dailyAttendanceHours")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#9CA3AF",
                  fontFamily: "Poppins, sans-serif",
                  mt: 0.3,
                }}
              >
                 {t("reportsScreen.hoursWorkedPerDay")}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenDetailModal(true)}>
              <img src={mapicon} alt="details" style={{ width: 33, height: 33 }} />
            </IconButton>
          </Box>

          {chartData.bar.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.bar} barCategoryGap="20%">
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="hours"
                  name={t("reportsScreen.hoursWorked")}
                  fill="#42A5F5"
                  barSize={40}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box height={300} display="flex" alignItems="center" justifyContent="center">
              <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
            </Box>
          )}
        </Card>

        {/* Pie Chart - Validation Status */}
        <Card
          sx={{
            flex: "1 1 48%",
            minWidth: 300,
            p: 2,
            border: "2px solid #091E4224",
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#44546F",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {t("reportsScreen.attendanceValidationStatus")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#9CA3AF",
                  fontFamily: "Poppins, sans-serif",
                  mt: 0.3,
                }}
              >
                {t("reportsScreen.validationStatusDesc")
    .split(",")
    .map(item =>
      item
        .trim()
        .replace(/_/g, " ")
        .replace(/\b\w/g, char => char.toUpperCase())
    )
    .join(", ")}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenDetailModal(true)}>
              <img src={mapicon} alt="details" style={{ width: 33, height: 33 }} />
            </IconButton>
          </Box>

          {chartData.pie.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.pie.map((d) => ({
                    ...d,
                    name: t(`monthlySummary.statuses.${d.status}`),
                  }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={4}
                  dataKey="value"
                  label={renderPieLabel}
                >
                  {chartData.pie.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ fontFamily: "Poppins, sans-serif", fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box height={300} display="flex" alignItems="center" justifyContent="center">
              <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
            </Box>
          )}
        </Card>
      </Box>

      {/* Detail Modal with Table */}
      <Modal open={openDetailModal} onClose={() => setOpenDetailModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            width: "90%",
            maxWidth: 900,
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#003149", fontFamily: "Poppins, sans-serif" }}
            >
              {t("reportsScreen.attendanceDetails")}
            </Typography>
            <IconButton onClick={() => setOpenDetailModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {summary && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "#F5F5F5", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: "Poppins, sans-serif" }}>
                <strong>{t("reportsScreen.totalDays")}:</strong> {summary.records_count} |{" "}
                <strong>{t("reportsScreen.hoursWorked")}:</strong> {chartData.totalHours} hrs
              </Typography>
            </Box>
          )}

          {reportData.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                      {t("attendanceLabels.table.date")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                      {t("attendanceLabels.table.checkIn")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                     {t("attendanceLabels.table.checkOut")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                      {t("attendanceLabels.table.hoursWorked")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
                      {t("attendanceLabels.table.status")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((record, index) => {
                    const statusColors = getStatusColor(record.validation_status);
                    return (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontFamily: "Poppins, sans-serif" }}>
                          {formatDate(record.date)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Poppins, sans-serif", color: "#2E7D32" }}>
                          {formatTime(record.check_in)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Poppins, sans-serif", color: "#C62828" }}>
                          {formatTime(record.check_out)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Poppins, sans-serif" }}>
                          {formatHours(record.total_hours)}
                        </TableCell>
                        <TableCell>
                        <Chip
  label={
    t(
      `attendanceLabels.status.${record.validation_status?.toLowerCase()}`,
      record.validation_status
        ? record.validation_status
            .replace(/_/g, " ")
            .replace(/\b\w/g, c => c.toUpperCase())
        : t("attendanceLabels.status.unknown")
    )
  }
  size="small"
  sx={{
    bgcolor: statusColors.bg,
    color: statusColors.color,
    fontFamily: "Poppins, sans-serif",
    fontSize: "11px",
  }}
/>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ textAlign: "center", py: 4 }}>{t("reportsScreen.noData")}</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default WorkerAttendanceReport;
